import React, { useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import * as XLSX from "xlsx";
import { FaFileExcel } from "react-icons/fa";
import { Tooltip as InfoTooltip } from "react-tooltip";
import { Info } from "lucide-react";

const Wrapper = styled.div`
  width: 100%;
`;

const Controls = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 16px;
`;

const Select = styled.select`
  padding: 8px 12px;
  font-size: 15px;
  border: 1px solid #ccc;
  border-radius: 4px;
`;

const SearchInput = styled.input`
  padding: 8px 12px;
  font-size: 15px;
  border: 1px solid #ccc;
  border-radius: 4px;
  flex-grow: 1;
  max-width: 300px;
`;

const ExportButton = styled.button`
  display: flex;
  align-items: center;
  padding: 8px 12px;
  background-color: #1d6f42;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;

  &:hover { background-color: #155e36; }
  svg { margin-right: 6px; }
`;

const TableWrapper = styled.div`
  overflow-x: auto;
`;

const StyledTable = styled.table`
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  border: 1px solid #ccc;
  border-radius: 8px;
  overflow: hidden;
  margin-top: 12px;
  background-color: #fff;
  font-size: 0.7rem;

  th, td {
    border: 1px solid #ddd;
    text-align: center;
    padding: 14px 8px;
    line-height: 1.6;
  }

  th { background: #d5b6ec; }

  tr:nth-child(even) { background-color: #f9f9f9; }
  tr:hover { background-color: #eef6ff; }

  .positive { background-color: #e7f9ed; color: #2e7d32; font-weight: 500; }
  .negative { background-color: #ffeaea; color: #c62828; font-weight: 500; }
  .bold { font-weight: bold; }

  .highlight-top { background-color: rgba(0,128,0,0.1); font-weight: bold; }
  .highlight-low { background-color: rgba(255,0,0,0.1); font-weight: bold; }
  .highlight-neutral { background-color: #f0f0f0; }

  .rank-up { color: green; font-weight: bold; }
  .rank-down { color: red; font-weight: bold; }
  .rank-neutral { color: #666; }
`;

const ChartTitle = styled.div`
  font-family: Arial, sans-serif;
  font-size: 12px;
  font-style: normal;
  font-weight: bold;
  color: #666;
  margin-bottom: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
`;

const InfoIconWrapper = styled.div`
  cursor: pointer;
  color: #666;
  transition: color 0.3s;
  &:hover { color: #000; }
  svg { width: 18px; height: 18px; }
`;

/* === Pagination UI === */
const PaginationBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-top: 12px;
  flex-wrap: wrap;
`;

const RowsGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;

  select {
    padding: 6px 10px;
    border: 1px solid #d0d0d0;
    border-radius: 4px;
    font-size: 13px;
    background: #fff;
  }
`;

const PaginationContainer = styled.div`
  display: flex;
  justify-content: center;
  gap: 6px;
`;

const PageButton = styled.button`
  min-width: 34px;
  height: 28px;
  border: 1px solid #d0d0d0;
  background: ${(p) => (p.$active ? "#d5b6ec" : "#f7f7f7")};
  color: #111;
  border-radius: 2px;
  cursor: pointer;
  font-size: 12px;

  &:hover { background: ${(p) => (p.$active ? "#d5b6ec" : "#eaeaea")}; }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

const RangeInfo = styled.div`
  font-size: 13px;
  color: #333;
`;

const formatPLN = (value) =>
  new Intl.NumberFormat("pl-PL", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(value);

const ZakupyByPolishRegionsTable = ({ purchaseData = [], clientsData = [] }) => {
  const [selectedYear, setSelectedYear] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(null);
  const [search, setSearch] = useState("");

  /* Pagination state */
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  useEffect(() => {
    const years = [...new Set(purchaseData.map((r) => parseInt(r.Rok)))].sort((a, b) => b - a);
    setSelectedYear(years[0]);
    const months = purchaseData.filter((r) => parseInt(r.Rok) === years[0]).map((r) => parseInt(r.Miesiac));
    setSelectedMonth([...new Set(months)].sort((a, b) => b - a)[0]);
  }, [purchaseData]);

  /* Build full region dataset */
const regionData = useMemo(() => {
  if (!selectedYear || selectedMonth === null) return [];

  const includeFullYear = selectedMonth === 0;
  const prevMonth = selectedMonth === 1 ? 12 : selectedMonth - 1;
  const prevYear = selectedMonth === 1 ? selectedYear - 1 : selectedYear;
  const yearAgo = selectedYear - 1;

  // map NIP -> region
  const nipToRegion = {};
  for (const c of clientsData) {
    if (c.nip?.trim()) nipToRegion[c.nip.trim()] = c.wojewodztwo || "Nieznany";
  }

  // group by region
  const grouped = {};
  for (const row of purchaseData) {
    const nip = row.NrDostawcy?.trim();
    const region = nipToRegion[nip] || "Nieznany";
    if (!grouped[region]) grouped[region] = [];
    grouped[region].push({
      rok: parseInt(row.Rok),
      miesiac: parseInt(row.Miesiac),
      value: (Number(row.K_42) || 0) + (Number(row.K_43) || 0),
    });
  }

  // compute metrics for ALL regions (no search yet)
  const resultsAll = Object.entries(grouped)
    .map(([region, rows]) => {
      const current = rows
        .filter(r => r.rok === selectedYear && (includeFullYear || r.miesiac === selectedMonth))
        .reduce((s, r) => s + r.value, 0);

      const prev = includeFullYear
        ? 0
        : rows
            .filter(r => r.rok === prevYear && r.miesiac === prevMonth)
            .reduce((s, r) => s + r.value, 0);

      const yoy = rows
        .filter(r => r.rok === yearAgo && (includeFullYear || r.miesiac === selectedMonth))
        .reduce((s, r) => s + r.value, 0);

      return {
        region,
        current,
        prev,
        yoy,
        mmChange: includeFullYear || prev === 0 ? null : ((current - prev) / prev) * 100,
        yyChange: yoy === 0 ? null : ((current - yoy) / yoy) * 100,
      };
    })
    .filter(r => r.region !== "Nieznany");

  // ranks based on FULL set (stable regardless of search)
  const rankedNowAll = [...resultsAll].sort((a, b) => b.current - a.current);
  const rankMap = new Map(rankedNowAll.map((r, i) => [r.region, i + 1]));

  const rankedPrevAll = [...resultsAll].sort((a, b) => b.yoy - a.yoy);
  const prevRankMap = new Map(rankedPrevAll.map((r, i) => [r.region, i + 1]));

  const finalAll = resultsAll.map(r => {
    const rank = rankMap.get(r.region) ?? null;
    const prevRank = prevRankMap.get(r.region) ?? null;
    const rankChange = (rank && prevRank) ? (prevRank - rank) : null;
    return { ...r, rank, prevRank, rankChange };
  });

  // apply search AFTER ranks are fixed, then sort by rank
  const filtered = finalAll.filter(r =>
    r.region.toLowerCase().includes((search || "").toLowerCase())
  );

  return filtered.sort((a, b) => (a.rank ?? 1e9) - (b.rank ?? 1e9));
}, [purchaseData, clientsData, selectedYear, selectedMonth, search]);

  /* Pagination derived values */
  const totalPages = Math.max(1, Math.ceil(regionData.length / rowsPerPage));
  const startIndex = (currentPage - 1) * rowsPerPage;
  const endIndex = Math.min(startIndex + rowsPerPage, regionData.length);
  const paginatedRows = useMemo(() => regionData.slice(startIndex, endIndex), [regionData, startIndex, endIndex]);

  /* Reset page when filters/search/rowsPerPage change */
  useEffect(() => { setCurrentPage(1); }, [selectedYear, selectedMonth, search, rowsPerPage]);

  const exportToExcel = () => {
    const rows = regionData.map((r) => ({
      Województwo: r.region,
      Ranking: r.rank,
      "Ranking r-1": r.prevRank || "-",
      "Zmiana rankingu":
        r.rankChange != null
          ? r.rankChange > 0 ? `↑ ${r.rankChange}` : r.rankChange < 0 ? `↓ ${Math.abs(r.rankChange)}` : "–"
          : "-",
      "Wartość bieżący m-c": r.current.toFixed(2),
      "Wartość poprzedni m-c": r.prev.toFixed(2),
      "Wartość r-1": r.yoy.toFixed(2),
      "m/m (%)": r.mmChange === null ? "—" : `${r.mmChange.toFixed(2)}%`,
      "r/r (%)": r.yyChange === null ? "—" : `${r.yyChange.toFixed(2)}%`,
    }));
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "WojewodztwaZakup");
    XLSX.writeFile(wb, "wojewodztwa_zakupy.xlsx");
  };

  const getMonthName = (num) =>
    new Date(2000, num - 1).toLocaleString("pl-PL", { month: "long" });

  const availableYears = [...new Set(purchaseData.map((r) => parseInt(r.Rok)))].sort((a, b) => b - a);
  const availableMonths = [...new Set(
    purchaseData.filter((r) => parseInt(r.Rok) === selectedYear).map((r) => parseInt(r.Miesiac))
  )].sort((a, b) => b - a);

  return (
    <Wrapper>
      <ChartTitle>
        Województwa wg wartości zakupów
        <InfoIconWrapper data-tooltip-id="regions-purchases-tooltip">
          <Info />
        </InfoIconWrapper>
      </ChartTitle>
      <InfoTooltip
        id="regions-purchases-tooltip"
        place="left"
        content={
          <>
            <strong>Ranking województw</strong><br />
            Tabela pokazuje wszystkie województwa z paginacją.
          </>
        }
      />

      <Controls>
        <Select value={selectedYear || ""} onChange={(e) => setSelectedYear(parseInt(e.target.value))}>
          <option value="">Rok</option>
          {availableYears.map((y) => <option key={y} value={y}>{y}</option>)}
        </Select>

        <Select value={selectedMonth ?? ""} onChange={(e) => setSelectedMonth(parseInt(e.target.value))}>
          <option value={0}>Cały rok</option>
          {availableMonths.map((m) => <option key={m} value={m}>{getMonthName(m)}</option>)}
        </Select>

        <SearchInput
          type="text"
          placeholder="Szukaj województwa..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />

        <ExportButton onClick={exportToExcel}>
          <FaFileExcel /> Eksportuj do Excel
        </ExportButton>
      </Controls>

      <TableWrapper>
        <StyledTable>
          <thead>
            <tr>
              <th>Województwo</th>
              <th>Wartość bieżący m-c</th>
              <th>Wartość poprzedni m-c</th>
              <th>Wartość r-1</th>
              <th>m/m (%)</th>
              <th>r/r (%)</th>
              <th>Ranking</th>
              <th>Ranking r-1</th>
              <th>Zmiana rankingu</th>
            </tr>
          </thead>
          <tbody>
            {paginatedRows.map((row, idx) => (
              <tr key={`${row.region}-${idx}`}>
                <td className="bold">{row.region}</td>
                <td>{formatPLN(row.current)}</td>
                <td>{formatPLN(row.prev)}</td>
                <td>{formatPLN(row.yoy)}</td>
                <td className={`bold ${row.mmChange > 0 ? "positive" : row.mmChange < 0 ? "negative" : ""}`}>
                  {row.mmChange === null ? "—" : `${row.mmChange.toFixed(2)}%`}
                </td>
                <td className={`bold ${row.yyChange > 0 ? "positive" : row.yyChange < 0 ? "negative" : ""}`}>
                  {row.yyChange === null ? "—" : `${row.yyChange.toFixed(2)}%`}
                </td>
                <td className={row.rank <= 3 ? "highlight-top" : row.rank >= 8 ? "highlight-low" : "highlight-neutral"}>
                  {row.rank}
                </td>
                <td className={row.prevRank <= 3 ? "highlight-top" : row.prevRank >= 8 ? "highlight-low" : "highlight-neutral"}>
                  {row.prevRank || "-"}
                </td>
                <td className={row.rankChange > 0 ? "rank-up" : row.rankChange < 0 ? "rank-down" : "rank-neutral"}>
                  {row.rankChange !== null
                    ? row.rankChange > 0 ? `↑ ${row.rankChange}` :
                      row.rankChange < 0 ? `↓ ${Math.abs(row.rankChange)}` : "–"
                    : "-"}
                </td>
              </tr>
            ))}
          </tbody>
        </StyledTable>
      </TableWrapper>

    </Wrapper>
  );
};

export default ZakupyByPolishRegionsTable;
